import axios, { AxiosInstance } from 'axios';
import React from 'react';
import { useCookies } from 'react-cookie';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import isEmail from 'validator/lib/isEmail';

import { useGetPublicShowroomFieldsQuery, useGetPublicShowroomLazyQuery } from 'generated/graphql';

import { getImageUrl, getVersion } from 'lib/cloudinary';
import { useIdentifyUser } from 'lib/identifyUser';
import { useUserConsent } from 'lib/userConsent';

import ShowroomContext from 'context/showroom/';

import CookieBanner from 'components/Common/CookieBanner';
import Error from 'components/Common/Error';
import LoadingAnimation from 'components/Common/LoadingAnimation';
import ShowroomContent from 'components/Showroom/Content';
import PublicShowroomLogin from 'components/Showroom/Login';

const trackApiConfig = {
	baseURL: `${process.env.REACT_APP_API_URL}`
};

const PublicShowroom = () => {
	const { id, tenant } = useParams();
	const [cookies, setCookie] = useCookies();
	const { acceptConsent, renderInput, onSubmit: onSubmitFormConsent } = useUserConsent();
	const identify = useIdentifyUser();

	const { i18n } = useTranslation();

	const [getPublicShowroom, { loading, error, data: showroomData }] = useGetPublicShowroomLazyQuery();

	const {
		loading: loadingPublicFields,
		error: errorPublicFields,
		data: dataPublicFields
	} = useGetPublicShowroomFieldsQuery({ variables: { id, tenant } });

	React.useEffect(() => {
		const password = cookies[`showroom-${tenant}-${id}`] ? cookies[`showroom-${tenant}-${id}`].showroomPassword : '';

		getPublicShowroom({ variables: { id, tenant, password } });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [password, setPassword] = React.useState('');
	const [wrongPassword, setWrongPassword] = React.useState(false);
	const [email, setEmail] = React.useState('');
	const [wrongEmail, setWrongEmail] = React.useState(false);
	const [isCheckPasswordQuery, setIsCheckPasswordQuery] = React.useState(false);
	const [customLogo, setCustomLogo] = React.useState('');
	const [customTitle, setCustomTitle] = React.useState('');
	const [customDescription, setCustomDescription] = React.useState('');

	const _ajax: AxiosInstance = React.useMemo(() => {
		return axios.create({ ...trackApiConfig });
	}, []);

	const HelmetComponent = React.useMemo(
		() => (
			<Helmet>
				{customTitle ? <title>{customTitle}</title> : null}
				{customLogo ? <link rel="icon" href={customLogo} id="favicon" type="image/x-icon" /> : null}
				{customDescription ? <meta name="description" content={customDescription} /> : null}
			</Helmet>
		),
		[customTitle, customLogo, customDescription]
	);

	React.useEffect(() => {
		if (!showroomData && !dataPublicFields) return;

		if (showroomData) {
			const publicId = showroomData.getShowroom.library?.logo?.publicId ?? showroomData.getShowroom.tenant.logo?.publicId;
			const publicUrl = showroomData.getShowroom.library?.logo?.publicUrl ?? showroomData.getShowroom.tenant.logo?.publicUrl;
			if (publicId && publicUrl) {
				setCustomLogo(
					getImageUrl(publicId, {
						width: 72,
						height: 72,
						version: getVersion(publicUrl)
					})
				);
			}
		} else {
			if (dataPublicFields?.publicShowroomFields?.logo) {
				setCustomLogo(
					getImageUrl(dataPublicFields.publicShowroomFields.logo, {
						width: 72,
						height: 72,
						version: getVersion(dataPublicFields.publicShowroomFields.logo)
					})
				);
			}
		}

		const language = showroomData ? showroomData.getShowroom.language : dataPublicFields.publicShowroomFields.language;
		if (language) i18n.changeLanguage(language);

		const tenantName = showroomData ? showroomData.getShowroom.tenant.name : tenant;
		const collection = showroomData ? showroomData.getShowroom.libraryCollection : dataPublicFields.publicShowroomFields.libraryCollection;
		const season = showroomData ? showroomData.getShowroom.librarySeason : dataPublicFields.publicShowroomFields.librarySeason;

		setCustomTitle(`${tenantName} ${season ? ` | ${season}` : ''} ${collection ? collection : ''}`);
		setCustomDescription(`Digital showroom by ${tenantName} (powered by Launchmetrics)`);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dataPublicFields, showroomData, tenant]);

	const generateUserViewer = React.useCallback(async () => {
		const newId = (await _ajax.post('generateUserViewer', { tenant })).data;
		// Cookie duration: 1 month (30 days)
		setCookie(
			`showroom-${tenant}`,
			{ ...cookies[`showroom-${tenant}`], userViewerId: newId, userViewerEmail: !!email ? email : undefined },
			{ maxAge: 2592000 }
		);
		const tenantData = showroomData?.getShowroom.tenant;
		if (email) {
			console.log('Called only if the user has an email');
			identify(
				{ id: email, email, externalGuest: true },
				{
					ecoAccountId: tenantData?.EcosystemAccountID,
					EcoAccountName: tenantData?.EcosystemAccountName,
					SalesforceId: tenantData?.salesforceAccountId,
					sfparentid: tenantData?.salesforceAccountParentId,
					Site: tenantData?.name,
					siteId: tenantData?.id
				}
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [_ajax, tenant, email]);

	React.useEffect(() => {
		if (password && !error && !!showroomData) {
			setCookie(`showroom-${tenant}-${id}`, { showroomPassword: password }, { maxAge: 2592000 });
			acceptConsent(email);
		}

		if (showroomData) {
			const checkUserViewerId = async () => {
				const id = cookies[`showroom-${tenant}`] ? cookies[`showroom-${tenant}`].userViewerId : null;
				if (!id) {
					try {
						await generateUserViewer();
					} catch (err) {
						console.error(err);
						await generateUserViewer();
					}
				}
			};
			checkUserViewerId();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error, id, password, showroomData, tenant]);

	React.useEffect(() => {
		if (!loading && error && isCheckPasswordQuery && !wrongPassword) {
			setWrongPassword(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading]);

	const passwordBackground = React.useMemo(() => {
		if (!dataPublicFields)
			return {
				type: '',
				src: ''
			};
		const publicFields = dataPublicFields.publicShowroomFields;
		if (publicFields.heroMedia && publicFields.heroMedia.publicUrl)
			return {
				type: publicFields.heroMedia.mediaType,
				src: publicFields.heroMedia.publicUrl
			};
		else if (publicFields.defaultBackground)
			return {
				type: publicFields.defaultBackground.mediaType,
				src: publicFields.defaultBackground.publicUrl
			};
		else
			return {
				type: 'image',
				src: publicFields.logo
			};
	}, [dataPublicFields]);

	const handleSubmitPassword = React.useCallback(() => {
		onSubmitFormConsent(() => {
			if (password.length) {
				getPublicShowroom({ variables: { id, tenant, password } });
				if (!isCheckPasswordQuery) setIsCheckPasswordQuery(true);
			}
		});
	}, [getPublicShowroom, id, isCheckPasswordQuery, onSubmitFormConsent, password, tenant]);

	const ShowroomLogin = React.useMemo(() => {
		if (!dataPublicFields) return null;
		const publicFields = dataPublicFields.publicShowroomFields;
		return (
			<PublicShowroomLogin
				publicFields={publicFields}
				background={passwordBackground}
				email={email}
				password={password}
				wrongEmail={wrongEmail}
				wrongPassword={wrongPassword}
				loading={loading}
				renderUserConsentInput={renderInput}
				onChangeEmail={email => {
					setEmail(email);
					if (!isEmail(email)) return setWrongEmail(true);
					setWrongEmail(false);
				}}
				onChangePassword={password => {
					setWrongPassword(false);
					setPassword(password);
				}}
				onSubmit={handleSubmitPassword}
			/>
		);
	}, [dataPublicFields, passwordBackground, email, password, wrongEmail, wrongPassword, loading, renderInput, handleSubmitPassword]);

	if (!loadingPublicFields && errorPublicFields) return <Error />;
	if ((loading || loadingPublicFields) && !isCheckPasswordQuery) return <LoadingAnimation />;

	if ((!showroomData && dataPublicFields) || (isCheckPasswordQuery && !showroomData)) {
		return (
			<>
				{HelmetComponent}
				{ShowroomLogin}
				<CookieBanner />
			</>
		);
	}

	const { getShowroom: showroom } = showroomData;
	return (
		<>
			{HelmetComponent}
			<ShowroomContext
				showroomId={showroom.id}
				isUserView
				tenant={tenant}
				password={cookies[`showroom-${tenant}-${id}`]?.showroomPassword}
				isPublic={true}
			>
				<ShowroomContent />
			</ShowroomContext>
			<CookieBanner />
		</>
	);
};

export default React.memo(PublicShowroom);
